// Keys that map to listOptions in the context
export const CONFIGURABLE_OPTION_KEYS = {
    BACKGROUND_COLOR: "backgroundColor",
    CONTAINER_TYPE: "containerType",
    TEXT_COLOR: "textColor",
    FONT_SIZE: "fontSize",
    TEXT_ALIGN: "textAlignment",
    SPACING: "spacing",
    BORDER_RADIUS: "borderRadius",
    OBJECT_FIT: "objectFit",
    ASPECT_RATIO: "aspectRatio",
    SHADOW: "shadow",
    WRAPPER_TYPE: "wrapperType",
    JUSTIFY_CONTENT: "justifyContent",
};
// Define style fields for each content block type
export const CONTENT_BLOCK_STYLE_CONFIGS = {
    pageSection: {
        label: "Page Section",
        fields: [
            {
                name: "backgroundColor",
                label: "Background Color",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.BACKGROUND_COLOR,
            },
            {
                name: "containerType",
                label: "Container Type",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.CONTAINER_TYPE,
            },
            {
                name: "textColor",
                label: "Text Color",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.TEXT_COLOR,
            },
            {
                name: "containerPadding",
                label: "Container Padding",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.SPACING,
            },
            {
                name: "titlePadding",
                label: "Title Padding",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.SPACING,
            },
            {
                name: "wrapperType",
                label: "Wrapper Type",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.WRAPPER_TYPE,
            },
        ],
    },
    textBlock: {
        label: "Text Block",
        fields: [
            {
                name: "textColor",
                label: "Text Color",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.TEXT_COLOR,
            },
            {
                name: "fontSize",
                label: "Font Size",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.FONT_SIZE,
            },
            {
                name: "textAlign",
                label: "Text Alignment",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.TEXT_ALIGN,
            },
            {
                name: "padding",
                label: "Padding",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.SPACING,
            },
            {
                name: "justifyContent",
                label: "Vertical Alignment",
                type: "select",
                options: [
                    { label: "Top", value: "justify-start" },
                    { label: "Center", value: "justify-center" },
                    { label: "Bottom", value: "justify-end" },
                ],
                defaultValue: "justify-center",
            },
            {
                name: "horizontalAlign",
                label: "Horizontal Alignment",
                type: "select",
                options: [
                    { label: "Left", value: "items-start" },
                    { label: "Center", value: "items-center" },
                    { label: "Right", value: "items-end" },
                ],
                defaultValue: "items-start",
            },
        ],
    },
    cardGrid: {
        label: "Card Grid",
        fields: [
            {
                name: "columns",
                label: "Columns",
                type: "select",
                options: [
                    { label: "2 Columns", value: "2" },
                    { label: "3 Columns", value: "3" },
                    { label: "4 Columns", value: "4" },
                ],
                defaultValue: "3",
            },
            {
                name: "gap",
                label: "Gap (px)",
                type: "number",
                placeholder: "e.g. 24",
                defaultValue: 24,
            },
            {
                name: "cardBackgroundColor",
                label: "Card Background",
                type: "color",
                defaultValue: "#ffffff",
            },
        ],
    },
    media: {
        label: "Media",
        fields: [
            {
                name: "borderRadius",
                label: "Rounded Corners",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.BORDER_RADIUS,
            },
            {
                name: "objectFit",
                label: "Object Fit",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.OBJECT_FIT,
            },
            {
                name: "objectPosition",
                label: "Image Position",
                type: "select",
                options: [
                    { label: "Center", value: "object-center" },
                    { label: "Top", value: "object-top" },
                    { label: "Bottom", value: "object-bottom" },
                    { label: "Left", value: "object-left" },
                    { label: "Right", value: "object-right" },
                ],
                defaultValue: "object-center",
            },
            {
                name: "aspectRatio",
                label: "Aspect Ratio",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.ASPECT_RATIO,
            },
            {
                name: "maxWidth",
                label: "Max Width",
                type: "select",
                options: [
                    { label: "Auto", value: "w-auto" },
                    { label: "Full", value: "max-w-full" },
                    { label: "Small (320px)", value: "max-w-xs" },
                    { label: "Medium (448px)", value: "max-w-md" },
                    { label: "Large (512px)", value: "max-w-lg" },
                    { label: "XL (576px)", value: "max-w-xl" },
                    { label: "2XL (672px)", value: "max-w-2xl" },
                ],
                showWhen: { field: "adjustable", notValue: true },
            },
            {
                name: "verticalAlign",
                label: "Vertical Alignment",
                type: "select",
                options: [
                    { label: "Top", value: "items-start" },
                    { label: "Center", value: "items-center" },
                    { label: "Bottom", value: "items-end" },
                ],
                defaultValue: "items-center",
                showWhen: { field: "adjustable", notValue: true },
            },
            {
                name: "horizontalAlign",
                label: "Horizontal Alignment",
                type: "select",
                options: [
                    { label: "Left", value: "justify-start" },
                    { label: "Center", value: "justify-center" },
                    { label: "Right", value: "justify-end" },
                ],
                defaultValue: "justify-center",
                showWhen: { field: "adjustable", notValue: true },
            },
            {
                name: "adjustable",
                label: "Enable Position Adjustments",
                type: "toggle",
                defaultValue: false,
            },
            {
                name: "scale",
                label: "Scale",
                type: "range",
                min: 50,
                max: 200,
                step: 5,
                defaultValue: 100,
                unit: "%",
                showWhen: { field: "adjustable", value: true },
            },
            {
                name: "positionX",
                label: "Horizontal Offset",
                type: "range",
                min: -100,
                max: 100,
                step: 5,
                defaultValue: 0,
                unit: "%",
                showWhen: { field: "adjustable", value: true },
            },
            {
                name: "positionY",
                label: "Vertical Offset",
                type: "range",
                min: -100,
                max: 100,
                step: 5,
                defaultValue: 0,
                unit: "%",
                showWhen: { field: "adjustable", value: true },
            },
            {
                name: "showDescription",
                label: "Show Description",
                type: "toggle",
                defaultValue: false,
            },
            {
                name: "descriptionOffsetX",
                label: "Description Horizontal Offset",
                type: "range",
                min: -50,
                max: 50,
                step: 5,
                defaultValue: 0,
                unit: "%",
                showWhen: { field: "showDescription", value: true },
            },
            {
                name: "descriptionOffsetY",
                label: "Description Vertical Offset",
                type: "range",
                min: -50,
                max: 50,
                step: 5,
                defaultValue: 0,
                unit: "%",
                showWhen: { field: "showDescription", value: true },
            },
            {
                name: "descriptionTextAlign",
                label: "Description Text Align",
                type: "select",
                options: [
                    { label: "Left", value: "text-left" },
                    { label: "Center", value: "text-center" },
                    { label: "Right", value: "text-right" },
                ],
                defaultValue: "text-left",
                showWhen: { field: "showDescription", value: true },
            },
        ],
    },
    customizableWrapper: {
        label: "Customizable Wrapper",
        fields: [
            {
                name: "textAlign",
                label: "Text Alignment",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.TEXT_ALIGN,
            },
            {
                name: "color",
                label: "Text Color",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.TEXT_COLOR,
            },
            {
                name: "fontSize",
                label: "Font Size",
                type: "select",
                optionsKey: CONFIGURABLE_OPTION_KEYS.FONT_SIZE,
            },
        ],
    },
};
// Default config for unknown content block types
export const DEFAULT_STYLE_CONFIG = {
    label: "Section",
    fields: [
        {
            name: "backgroundColor",
            label: "Background Color",
            type: "color",
            defaultValue: "#ffffff",
        },
        {
            name: "paddingTop",
            label: "Padding Top",
            type: "number",
            placeholder: "e.g. 40",
            defaultValue: 40,
        },
        {
            name: "paddingBottom",
            label: "Padding Bottom",
            type: "number",
            placeholder: "e.g. 40",
            defaultValue: 40,
        },
    ],
};
export const getStyleConfigForBlockType = (contentBlockType) => {
    return CONTENT_BLOCK_STYLE_CONFIGS[contentBlockType] || DEFAULT_STYLE_CONFIG;
};
// Define which fields are responsive (per-device) for each content block type
export const RESPONSIVE_FIELDS_BY_BLOCK_TYPE = {
    pageSection: ["containerType"],
    textBlock: ["padding", "justifyContent", "horizontalAlign"],
    media: [
        "objectFit",
        "objectPosition",
        "aspectRatio",
        "maxWidth",
        "containerShape",
        "verticalAlign",
        "horizontalAlign",
        "adjustable",
        "scale",
        "positionX",
        "positionY",
        "showDescription",
        "descriptionOffsetX",
        "descriptionOffsetY",
        "descriptionTextAlign",
    ],
};
// Field keys that represent padding values - these get "p-" prefix added
export const PADDING_FIELD_KEYS = ["padding", "titlePadding", "containerPadding"];
// Field keys that represent transform values - these get converted to Tailwind transform classes
export const TRANSFORM_FIELD_KEYS = {
    scale: "scale",
    positionX: "translate-x",
    positionY: "translate-y",
};
